import {ReactNode, useState} from 'react';
import {useLocation} from 'react-router-dom';

// eslint-disable-next-line no-restricted-imports -- Intended
import {append, assocPath, filter, findLastIndex} from 'ramda';
import {isError} from 'ramda-adjunct';

import {getRandomId} from 'shared/web';

import {ConfirmDialogProps} from '../../ConfirmDialog/ConfirmDialog';
import {ManagedConfirmDialogProps} from '../../ConfirmDialog/ManagedConfirmDialogProps';
import {DeleteDialogProps} from '../../DeleteDialog/DeleteDialog';
import {ManagedDeleteDialogProps} from '../../DeleteDialog/ManagedDeleteDialogProps';
import {ManagedSuccessDialogProps} from '../../SuccessDialog/ManagedSuccessDialogProps';
import {SuccessDialogProps} from '../../SuccessDialog/SuccessDialog';
import {DialogProps} from '../Dialog';
import {ManagedDialogProps} from '../ManagedDialogProps';

type DialogWithId<T> = T & {id: string};

type ManagedDialogItem =
  | {
      type: 'dialog';
      props: DialogWithId<DialogProps>;
    }
  | {
      type: 'delete';
      props: DialogWithId<DeleteDialogProps>;
    }
  | {
      type: 'confirm';
      props: DialogWithId<ConfirmDialogProps>;
    }
  | {
      type: 'success';
      props: DialogWithId<SuccessDialogProps>;
    };

type ManagedProps = {
  id?: string;
  onCloseComplete?: () => void;
};

export function useDialogsState() {
  /**
   * The useLocation hook is wrapped in attempt function
   * which serves as a try/catch block. It may thow an error
   * when there is no Router context. In such case, the automatic
   * closing of all dialogs on route change won't work.
   */
  const location = attempt(useLocation);
  const [dialogState, setDialogState] = useState({location, dialogs: NO_DIALOGS});

  if (!isError(location) && dialogState.location !== location) {
    setDialogState({location, dialogs: NO_DIALOGS});
  }

  const dialogs = dialogState.dialogs;
  const setDialogs = (update: (currentDialogs: ManagedDialogItem[]) => ManagedDialogItem[]) => {
    setDialogState((currentState) => ({
      ...currentState,
      dialogs: update(currentState.dialogs),
    }));
  };

  const addDialogItem = (dialogItem: ManagedDialogItem) => {
    setDialogs((dialogsState) =>
      append(
        dialogItem,
        filter((dialog) => dialog.props.id !== dialogItem.props.id, dialogsState)
      )
    );
  };

  const createManagedProps = <Props extends ManagedProps>(props: Props) => {
    const id = props.id ?? getRandomId();
    return {
      ...props,
      id,
      isOpen: true,
      onClose: () => closeDialog(id),
      onCloseComplete: () => {
        removeDialog(id);
        props.onCloseComplete?.();
      },
    };
  };

  const openDialog = (children: ReactNode, options?: ManagedDialogProps) => {
    addDialogItem({type: 'dialog', props: createManagedProps({...options, children})});
  };

  const openDeleteDialog = (options: ManagedDeleteDialogProps) => {
    addDialogItem({type: 'delete', props: createManagedProps(options)});
  };

  const openConfirmDialog = (options: ManagedConfirmDialogProps) => {
    addDialogItem({type: 'confirm', props: createManagedProps(options)});
  };

  const openSuccessDialog = (options: ManagedSuccessDialogProps) => {
    addDialogItem({type: 'success', props: createManagedProps(options)});
  };

  const closeDialog = (id: string) => {
    setDialogs((dialogsState) => {
      const index = dialogsState.findIndex((dialog) => dialog.props.id === id);
      return setDialogIndexToClose(index, dialogsState);
    });
  };

  const closeCurrentDialog = () => {
    setDialogs((dialogsState) => {
      const index = findLastIndex((dialog) => Boolean(dialog.props.isOpen), dialogsState);
      return setDialogIndexToClose(index, dialogsState);
    });
  };

  const removeDialog = (id: string) =>
    void setDialogs((dialogsState) => filter((dialog) => dialog.props.id !== id, dialogsState));

  return {
    dialogs,
    openDialog,
    openDeleteDialog,
    openConfirmDialog,
    openSuccessDialog,
    closeCurrentDialog,
    closeDialog,
  };
}

const NO_DIALOGS: ManagedDialogItem[] = [];

const setDialogIndexToClose = (index: number, dialogs: ManagedDialogItem[]) =>
  index < 0 ? dialogs : assocPath([index, 'props', 'isOpen'], false, dialogs);

const attempt = <Value>(fn: () => Value): Value | Error => {
  try {
    return fn();
  } catch (error: unknown) {
    return isError(error) ? error : new Error(String(error));
  }
};
